import { useEffect, useMemo } from 'react';
import { matchPath, generatePath, useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from '@ellucian/react-design-system/core';
import { useIntl } from 'react-intl';
import { spacing20, spacing40 } from '@ellucian/react-design-system/core/styles/tokens';
import { routes, adminRoutes, homePageMap } from '../Helpers/Configuration';
import { useTheme } from '@mui/material';
import useRoot from '../RootContext/useRoot';
import { createEDSTheme } from '@ellucian/react-design-system/core/themes';
import { Button, HeaderBar, HeaderBarLogo } from '@ellucian/react-design-system/core';
import Logo from './Logo';
import GlobalLocaleHeaderItems from '../GlobalLocale/GlobalLocaleHeaderItems';
import { isInquiryFormTemplate } from '@nsr/applicant-apply-shared/src/helpers/applicationTemplateHelper';
const useStyles = makeStyles({
    name: 'Header'
})((theme, { eteTheme } = {}) => {
    const setBackgroundImage = (image, color) => ({
        backgroundImage: image ? `url(${image}) !important` : 'none',
        backgroundColor: image ? 'transparent !important' : `${color} !important`,
    });
    return {
        headerBarLogoRoot: {
            display: 'flex',
            backgroundColor: 'transparent !important',
            overflow: 'hidden',
            minWidth: 0,
        },
        root: {
            left: 0,
            position: 'fixed',
            top: 0,
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            [theme.breakpoints.up('md')]: setBackgroundImage(eteTheme?.bgimage, theme.palette.primary.main),
            [theme.breakpoints.only('sm')]: setBackgroundImage(eteTheme?.portrait_bgimage || eteTheme?.bgimage, theme.palette.primary.main),
            [theme.breakpoints.down('md')]: {
                ...setBackgroundImage(eteTheme?.small_portrait_bgimage || eteTheme?.portrait_bgimage || eteTheme?.bgimage, theme.palette.primary.main),
                position: 'static',
            },
        },
        signOut: {
            display: 'flex',
        },
        username: {
            marginRight: spacing40,
            marginTop: spacing20,
            [theme.breakpoints.down('md')]: {
                display: 'none',
            },
        },
    };
});

const Header = props => {
    const intl = useIntl();
    const {
        showUtilityBar = true,
    } = props;
    const globalSettings = window?.PRELOADED_STATE?.globalSettings;
    const rootCtx = useRoot();
    const { eteTheme } = rootCtx;
    const { classes } = useStyles({ eteTheme });
    const history = useHistory();
    const location = useLocation();
    const theme = useTheme();
    const isThemed = useMemo(() => {
        const lightTheme = createEDSTheme('light');
        return theme.palette.primary.main !== lightTheme.palette.primary.main;
    }, [createEDSTheme, theme]);
    const applicationRouteMatch = useMemo(() => {
        if (typeof history.location.pathname !== 'string') {
            return null;
        }
        return matchPath(history.location.pathname, {
            path: [
                routes.applicationForm,
                routes.applicationFormPageById,
                routes.applicationFormReviewPage,
                routes.applicationFormStart,
                routes.applicationFormSubmitPage,
                routes.applicationFormWithdrawnPage,
                routes.applicationFormSupplementalItemsPage,
                routes.applicationFormPayments,
                routes.applicationFormSupplementalForm,
                routes.applicationShare,
                routes.externalApplication,
                routes.externalApplicationPageById,
                routes.externalApplicationSubmitPage,
                routes.externalApplicationSupplementalItemsPage,
                routes.externalApplicationPayments,
                routes.externalApplicationwReviewPage
            ]
        });
    }, [history.location.pathname]);

    useEffect(() => {
        const baseTitle = globalSettings?.applyPageTitle || '';

        const getSuffixFromIntl = id => {
            try {
                return ` - ${intl.formatMessage({ id })}`;
            } catch (_e) {
                return '';
            }
        };

        const getSuffix = () => {
            const path = history.location.pathname || window.location.pathname;

            // Retrieval page (global messages)
            if (path.startsWith('/applicant/retrieval')) {
                return getSuffixFromIntl('applicationRetrievalName');
            }

            // Inquiry pages (template language file / template messages)
            if (path.startsWith('/applicant/inquiry')) {
                return getSuffixFromIntl('inquiryName');
            }

            // Application form pages are handled elsewhere (ApplicationForm sets template title),
            // but if we are on a non-applicationRoute (e.g., selection, home), use global keys below.

            // UAE PASS login page
            if (path === routes.uaeLogin || path.startsWith(routes.uaeLogin)) {
                return getSuffixFromIntl('uaePassLoginHeading');
            }

            // New application route (/applicant/newapplication) — check this before home
            if (path.startsWith(routes.newApplication) || path === routes.newApplication) {
                return getSuffixFromIntl('applicationSelectionName');
            }
            if (path === routes.applicationSelection || path.startsWith('/applicant/application-selection')) {
                return getSuffixFromIntl('applicationSelectionName');
            }

            // Home route (use startsWith so '/applicant/' variants match)
            if (path.startsWith(routes.home)) {
                return getSuffixFromIntl('homeName');
            }

            return '';
        };

        if (!applicationRouteMatch) {
            document.title = `${baseTitle}${getSuffix()}`;
        }
    }, [applicationRouteMatch, globalSettings, history.location.pathname, intl]);

    const homeRoute = useMemo(() => {
        if (window.PRELOADED_STATE?.user) {
            const adminMatch = matchPath(location.pathname, adminRoutes);
            return adminMatch ? routes.admin : routes.home;
        }
        else {
            const formMatch = matchPath(location.pathname, {
                path: Object.keys(homePageMap)
            });

            if (formMatch)
                return generatePath(homePageMap[formMatch.path], formMatch.params);
        }

        return routes.retrieval;
    }, [location.pathname]);

    const UtilityBar = useMemo(() => {
        if (!showUtilityBar) {
            return (
                <GlobalLocaleHeaderItems isThemed={isThemed} />
            );
        }
        else return (
            <>
                {window.location.pathname.includes('/preview/') &&
                    <GlobalLocaleHeaderItems isThemed={isThemed} />
                }
            </>
        );
    }, [showUtilityBar, classes]);
    const logoSrc = useMemo(() => {
        return rootCtx?.eteTheme?.logo ?? null;
    }, [rootCtx.eteTheme]);
    const isExternalInquiry = isInquiryFormTemplate(
        window.PRELOADED_STATE?.externalApplication?.template?.templateType
    );
    const logoOnClickCallback = () => {
        if (!isExternalInquiry) {
            rootCtx.clearTheme();
        }
        history.push(homeRoute);
    };
    const isLogoBtnDisabled = useMemo(() => {
        return (history.location.pathname === routes.totp || history.location.pathname === routes.totpReset || history.location.pathname === routes.totpRecovery);
    }, [history.location.pathname]);
    return (
        <HeaderBar
            classes={{ root: classes.root, logoRoot: classes.headerBarLogoRoot }}
            logo={
                <HeaderBarLogo classes={{ root: classes.headerBarLogoRoot }} style={{ background: 'transparent' }}
                >
                    <Button
                        onClick={logoOnClickCallback}
                        classes={{ root: classes.headerBarLogoRoot }}
                        variant="text"
                        disabled={isLogoBtnDisabled}
                        aria-label={intl.formatMessage({ id: 'institutionLogoAltText' })}
                    >
                        <Logo src={logoSrc} />
                    </Button>
                </HeaderBarLogo>
            }
            position="static"
            showSearch={false}
            UtilityBar={UtilityBar}
        />
    );
};
export default Header;
